import invariant from 'tiny-invariant';
import { useCheckoutCartMachine } from './CheckoutCartMachineState';
import { ActorRefFrom, Sender, StateFrom } from '@monofront/xstate-v4';
import { ReactNode, createContext, useContext, useMemo } from 'react';
import {
  ISelectAddressMachineEvents,
  IAddressModalEvents,
  selectAddressMachine,
  addressModalMachine,
  selectShippingMethodMachine,
  selectPickupPointMachine,
  ISelectPickupPointEvents,
  ISelectShippingMethodEvents,
} from '@monofront/business/stateMachines';
import {
  ISelectTimeSlotsEvents,
  selectTimeSlotsMachine,
} from '@monofront/business/stateMachines/checkout/selectShippingMethod/selectTimeSlots';
import { useActor } from '@monofront/xstate-v4/react';

const CheckoutSelectAddressMachineContext = createContext<{
  sendToSelectAddress: Sender<ISelectAddressMachineEvents>;
  selectAddressState: StateFrom<typeof selectAddressMachine>;
  addressModalState: StateFrom<typeof addressModalMachine>;
  sendToAddressModal: Sender<IAddressModalEvents>;
  selectShippingMethodState: StateFrom<typeof selectShippingMethodMachine>;
  sendToSelectShippingMethod: Sender<ISelectShippingMethodEvents>;
  selectPickupPointState: StateFrom<typeof selectPickupPointMachine>;
  sendToSelectPickupPoint: Sender<ISelectPickupPointEvents>;
  selectTimeSlotsState: StateFrom<typeof selectTimeSlotsMachine>;
  sendToSelectTimeSlots: Sender<ISelectTimeSlotsEvents>;
} | null>(null);

export const CheckoutSelectAddressMachineProvider = ({
  children,
}: {
  children: ReactNode;
}) => {
  const { state } = useCheckoutCartMachine();

  invariant(
    state.context.selectAddressesRef,
    'selectAddressMachine should be initialised at this point',
  );
  const [selectAddressState, sendToSelectAddress] = useActor<
    ActorRefFrom<typeof selectAddressMachine>
  >(state.context.selectAddressesRef);

  invariant(
    selectAddressState.context.addressModalRef,
    'addressModalMachine should be initialised at this point',
  );
  const [addressModalState, sendToAddressModal] = useActor<
    ActorRefFrom<typeof addressModalMachine>
  >(selectAddressState.context.addressModalRef);

  invariant(
    state.context.selectShippingMethodRef,
    'selectShippingMethodMachine should be initialised at this point',
  );
  const [selectShippingMethodState, sendToSelectShippingMethod] = useActor<
    ActorRefFrom<typeof selectShippingMethodMachine>
  >(state.context.selectShippingMethodRef);

  invariant(
    selectShippingMethodState.context.selectPickupPointRef,
    'selectPickupPointMachine should be initialised at this point',
  );
  const [selectPickupPointState, sendToSelectPickupPoint] = useActor<
    ActorRefFrom<typeof selectPickupPointMachine>
  >(selectShippingMethodState.context.selectPickupPointRef);

  invariant(
    selectShippingMethodState.context.selectTimeSlotsRef,
    'selectTimeSlotsMachine should be initialised at this point',
  );
  const [selectTimeSlotsState, sendToSelectTimeSlots] = useActor<
    ActorRefFrom<typeof selectTimeSlotsMachine>
  >(selectShippingMethodState.context.selectTimeSlotsRef);

  return (
    <CheckoutSelectAddressMachineContext.Provider
      value={useMemo(
        () => ({
          sendToSelectAddress,
          selectAddressState,
          sendToSelectShippingMethod,
          addressModalState,
          sendToAddressModal,
          selectShippingMethodState,
          selectPickupPointState,
          sendToSelectPickupPoint,
          selectTimeSlotsState,
          sendToSelectTimeSlots,
        }),
        [
          sendToSelectAddress,
          selectAddressState,
          sendToSelectShippingMethod,
          addressModalState,
          sendToAddressModal,
          selectShippingMethodState,
          sendToSelectShippingMethod,
          selectPickupPointState,
          sendToSelectPickupPoint,
          selectTimeSlotsState,
          sendToSelectTimeSlots,
        ],
      )}
    >
      {children}
    </CheckoutSelectAddressMachineContext.Provider>
  );
};

export const useCheckoutSelectAddressMachine = () => {
  const ctx = useContext(CheckoutSelectAddressMachineContext);

  invariant(
    ctx,
    'CheckoutSelectAddressMachineContext should be present upper in the tree',
  );

  return ctx;
};
